import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { rxnormAPI } from '../services/api';
import SEO from '../components/SEO';

function RxNorm() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('rxnorm_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Save search to recent searches
  const addToRecentSearches = (query) => {
    const updated = [query, ...recentSearches.filter(q => q !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('rxnorm_recent_searches', JSON.stringify(updated));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await rxnormAPI.search(searchQuery);
      setResults(response.data);
      addToRecentSearches(searchQuery);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderResults = () => {
    if (!results) return null;

    if (results.error) {
      return (
        <div style={{ marginTop: '2rem' }}>
          <p style={{ color: '#e74c3c' }}>⚠️ {results.error}</p>
        </div>
      );
    }

    if (!results.drugs || results.drugs.length === 0) {
      return <p className="no-results">No drugs found for "{searchQuery}"</p>;
    }

    return (
      <div className="results-grid">
        {results.drugs.map((drug, index) => (
          <Link 
            to={`/rxnorm/${drug.rxcui}`} 
            key={index} 
            className="clickable-card" 
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div className="result-card hoverable-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>{drug.name}</h3>
                <span style={{ 
                  backgroundColor: '#3498db', 
                  color: 'white', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px', 
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>RxNorm</span>
              </div>
              <p><strong>RxCUI:</strong> {drug.rxcui}</p>
              <p><strong>Term Type:</strong> {drug.tty}</p>
            {drug.synonym && drug.synonym !== drug.name && (
              <p><strong>Synonym:</strong> {drug.synonym}</p>
            )}
            
            <div className="view-details-link">
              View Full Details →
            </div>
          </div>
          </Link>
        ))}
      </div>
    );
  };

  // SEO structured data
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    'name': 'RxNorm Drug Names',
    'description': 'Search RxNorm normalized naming system for clinical drugs',
    'specialty': 'Pharmaceutical Sciences'
  };

  return (
    <div className="page-container">
      <SEO
        title="RxNorm Drug Names - Standardized Clinical Drug Nomenclature"
        description="Search RxNorm, a normalized naming system for clinical drugs maintained by the National Library of Medicine. Find drug interactions, related medications, and standardized drug names."
        keywords="RxNorm, drug names, clinical drugs, drug nomenclature, drug interactions, National Library of Medicine, NLM, standardized drug names"
        canonical="/rxnorm"
        structuredData={seoStructuredData}
      />
      <h1 className="page-title">RxNorm Drug Names</h1>
      <p className="page-description">
        Search RxNorm, a normalized naming system for clinical drugs maintained by the National Library of Medicine. 
        Find drug interactions, related medications, and standardized drug names.
      </p>

      {recentSearches.length > 0 && (
        <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f0f4f8', borderRadius: '8px' }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '0.8rem' }}>Recent Searches</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {recentSearches.map((search, index) => (
              <button
                key={index}
                onClick={() => {
                  setSearchQuery(search);
                  // Simulate form submission
                  const event = new Event('submit', { bubbles: true });
                  event.preventDefault = () => {};
                  handleSearch(event);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
              >
                {search}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Enter drug name (e.g., aspirin, metformin, lisinopril)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">Error: {error}</div>}
      
      {loading && <div className="loading">Loading...</div>}

      {!loading && results && (
        <div className="results-section">
          <h2>Results ({results.count || 0})</h2>
          {renderResults()}
        </div>
      )}

      <div style={{ marginTop: '3rem', padding: '1.5rem', backgroundColor: '#ecf0f1', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>About RxNorm</h3>
        <p style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          RxNorm provides normalized names for clinical drugs and links them to many of the drug vocabularies 
          commonly used in pharmacy management and drug interaction software. It's maintained by the 
          National Library of Medicine (NLM) and is used widely in electronic health record systems.
        </p>
      </div>
    </div>
  );
}

export default RxNorm;
